import { Mic, MicOff, Video, VideoOff, Settings } from 'lucide-react';
import { cn } from '../lib/utils';

export interface DeviceControlButtonProps {
  /** Icon name from lucide-react */
  icon: 'mic' | 'mic-off' | 'video' | 'video-off' | 'settings';
  /** Whether the button is active (enabled state) */
  isActive?: boolean;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Button click handler */
  onClick?: () => void;
  /** Custom class name */
  className?: string;
  /** Accessible label */
  'aria-label'?: string;
}

const iconMap = {
  mic: Mic,
  'mic-off': MicOff,
  video: Video,
  'video-off': VideoOff,
  settings: Settings,
} as const;

const defaultAriaLabels: Record<DeviceControlButtonProps['icon'], string> = {
  mic: 'Toggle microphone on',
  'mic-off': 'Toggle microphone off',
  video: 'Toggle camera on',
  'video-off': 'Toggle camera off',
  settings: 'Open settings',
};

export const DeviceControlButton = ({
  icon,
  isActive = false,
  disabled = false,
  onClick,
  className,
  'aria-label': ariaLabel,
}: DeviceControlButtonProps) => {
  const Icon = iconMap[icon];
  const effectiveAriaLabel = ariaLabel ?? defaultAriaLabels[icon];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={effectiveAriaLabel}
      className={cn(
        'flex h-12 w-12 items-center justify-center rounded-xl border transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2',
        isActive
          ? 'border-transparent bg-orange-500 text-white hover:bg-orange-600'
          : 'border-slate-200 bg-slate-100 text-slate-500 hover:bg-slate-200',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      <Icon className="h-5 w-5" />
    </button>
  );
};
